import React, { useState, useEffect } from 'react';
import { User, Save, X, Loader } from 'lucide-react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const Settings = ({ user, onUserUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Check if there are changes from original user data
    const hasChange = value !== (user?.[name] || '');
    const otherField = name === 'name' ? 'email' : 'name';
    const otherHasChange = formData[otherField] !== (user?.[otherField] || '');
    
    setHasChanges(hasChange || otherHasChange);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      console.log('Sending profile update request:', {
        name: formData.name.trim(),
        email: formData.email.trim()
      });

      const response = await authAPI.updateProfile({
        name: formData.name.trim(),
        email: formData.email.trim()
      });

      console.log('Profile update response:', response);
      toast.success(response.message || 'Profile updated successfully!');
      
      // Update user data in parent component and localStorage
      const updatedUser = response.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }
      
      setHasChanges(false);
    } catch (error) {
      console.error('Profile update failed - Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      // Show specific error message if available
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update profile';
      toast.error(`Update failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || ''
      });
      setHasChanges(false);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div className="welcome-section">
          <h2>Settings</h2>
          <p>Manage your account settings and preferences</p>
        </div>
      </div>

      <div className="tool-content">
        <div className="tool-header">
          <User size={24} />
          <h3>Profile Information</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              name="name"
              className="form-input"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="email"
              className="form-input"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              required
            />
          </div>

          {hasChanges && (
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          )}

          {!hasChanges && (
            <div style={{ 
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              border: '1px solid #bbf7d0',
              color: '#166534'
            }}>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Your profile is up to date. Make changes above to save new information.
              </p>
            </div>
          )}
        </form>

        <div style={{ marginTop: '32px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
          <h4 style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>Account Information</h4>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            <p style={{ margin: '4px 0' }}>
              <strong>Member since:</strong> {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
            </p>
            <p style={{ margin: '4px 0' }}>
              <strong>Last login:</strong> {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
